import imageCompression from 'browser-image-compression';

/**
 * Compress and resize image to full size (max 1920x1080)
 * @param {File} file - Original image file
 * @returns {Promise<File>} - Compressed full-size image
 */
export const compressFullSizeImage = async (file) => {
  const options = {
    maxSizeMB: 1, // Max file size in MB
    maxWidthOrHeight: 1920, // Max dimension
    useWebWorker: true,
    fileType: 'image/jpeg', // Convert to JPEG for better compression
  };

  try {
    const compressedFile = await imageCompression(file, options);
    return compressedFile;
  } catch (error) {
    console.error('Error compressing full-size image:', error);
    throw error;
  }
};

/**
 * Generate thumbnail version of image (200x200)
 * @param {File} file - Original image file
 * @returns {Promise<File>} - Thumbnail image
 */
export const generateThumbnail = async (file) => {
  const options = {
    maxSizeMB: 0.1, // Very small file size for thumbnail
    maxWidthOrHeight: 200, // Thumbnail size
    useWebWorker: true,
    fileType: 'image/jpeg',
  };

  try {
    const thumbnailFile = await imageCompression(file, options);
    return thumbnailFile;
  } catch (error) {
    console.error('Error generating thumbnail:', error);
    throw error;
  }
};

/**
 * Process image: compress full size and generate thumbnail
 * @param {File} file - Original image file
 * @param {Function} onProgress - Progress callback (0-100)
 * @returns {Promise<{fullSize: File, thumbnail: File}>}
 */
export const processImage = async (file, onProgress) => {
  try {
    // Update progress: Starting compression
    if (onProgress) onProgress(10);

    // Compress full-size image
    const fullSize = await compressFullSizeImage(file);
    if (onProgress) onProgress(50);

    // Generate thumbnail
    const thumbnail = await generateThumbnail(file);
    if (onProgress) onProgress(90);

    if (onProgress) onProgress(100);

    return { fullSize, thumbnail };
  } catch (error) {
    console.error('Error processing image:', error);
    throw error;
  }
};

/**
 * Validate file size (max 5MB for original)
 * @param {File} file - File to validate
 * @returns {boolean} - True if valid
 */
export const validateFileSize = (file) => {
  const maxSizeMB = 5;
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  return file.size <= maxSizeBytes;
};

/**
 * Validate file type
 * @param {File} file - File to validate
 * @returns {boolean} - True if valid image type
 */
export const validateFileType = (file) => {
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  return validTypes.includes(file.type);
};
